import { type SyntheticEvent, useRef } from 'react';

import { Link } from '@tanstack/react-router';

import { AuthPasswordField } from '@nesso/ui/primitives/auth-password-field';
import { Button } from '@nesso/ui/primitives/button';
import { FieldError } from '@nesso/ui/primitives/field';
import { InputField } from '@nesso/ui/primitives/input-field';

import { useSignUpWithEmail } from '../lib/use-sign-up-with-email';
import { validationCopy } from '../model/sign-up-with-email.schema';

type Props = {
    signUpData?: string;
};

export function SignUpWithEmail({ signUpData }: Props) {
    const {
        email,
        password,
        errors,
        isSubmitting,
        setEmail,
        setPassword,
        handleEmailBlur,
        handlePasswordBlur,
        handleSubmit,
    } = useSignUpWithEmail(signUpData);

    const emailRef = useRef<HTMLInputElement>(null);
    const passwordRef = useRef<HTMLInputElement>(null);

    const onSubmit = async (event: SyntheticEvent<HTMLFormElement>) => {
        const fieldToFocus = await handleSubmit(event);
        if (fieldToFocus === 'email') emailRef.current?.focus();
        else if (fieldToFocus === 'password') passwordRef.current?.focus();
    };

    return (
        <form className="flex flex-col gap-6" noValidate onSubmit={onSubmit}>
            <InputField
                ref={emailRef}
                id="sign-up-email"
                type="email"
                label="Email"
                autoComplete="email"
                value={email}
                disabled={isSubmitting}
                error={errors.email}
                onChange={(event) => setEmail(event.target.value)}
                onBlur={handleEmailBlur}
            />

            <AuthPasswordField
                ref={passwordRef}
                id="sign-up-password"
                value={password}
                error={errors.password}
                disabled={isSubmitting}
                autoComplete="new-password"
                description={validationCopy.passwordComplexity}
                onChange={setPassword}
                onBlur={handlePasswordBlur}
            />

            {errors.form ? <FieldError>{errors.form}</FieldError> : null}

            <Button type="submit" size="lg" className="w-full" disabled={isSubmitting}>
                Create Account
            </Button>

            <p className="text-center text-sm text-muted-foreground">
                Already have an account?{' '}
                <Link to="/login" className="underline underline-offset-4">
                    Log in
                </Link>
            </p>
        </form>
    );
}
